import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ComboboxMulti } from "@/components/ui/combobox_mutli";
import {
  Task,
  CreateInstancesRequest,
  useCreateInstances,
} from "@/lib/api/checklist";
import { toast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { fetchApiGet } from "@/lib/api/utils";
import { DbUser } from "@/lib/models";
import { useListMinimalUsers } from "@/lib/api/users";

interface CreateInstancesModalProps {
  open: boolean;
  onClose: () => void;
  task: Task;
}

interface UsersResponse {
  data: DbUser[];
}

export function CreateInstancesModal({
  open,
  onClose,
  task,
}: CreateInstancesModalProps) {
  const createInstances = useCreateInstances();
  const minimalUserListResult = useListMinimalUsers();
  const users = (minimalUserListResult?.data?.data || []);

  const form = useForm<CreateInstancesRequest>({
    resolver: zodResolver(CreateInstancesRequest),
    defaultValues: {
      task_id: task.id,
      assignee_ids: [],
      due_date: "",
    },
  });

  const userOptions = users.map((user: DbUser) => ({
    value: user.id,
    label: user.name,
  }));

  const onSubmit = async (data: CreateInstancesRequest) => {
    try {
      const result = await createInstances.mutateAsync({
        ...data,
        due_date: data.due_date ? new Date(data.due_date).toISOString() : "",
      });
      if (!result?.success) {
        throw new Error(result?.message || "Failed to create instances");
      }
      toast({
        title: "Instances created",
        description: `Created ${data.assignee_ids.length} instances successfully.`,
      });
      onClose();
      form.reset();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create instances.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-xl">Create Task Instances</DialogTitle>
          <DialogDescription className="text-base">
            Create instances of "{task.title}" for multiple assignees
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pt-4">
            <FormField
              control={form.control}
              name="assignee_ids"
              render={({ field }) => (
                <FormItem className="space-y-3 grid">
                  <FormLabel className="text-sm font-medium">Assign to Users</FormLabel>
                  <FormControl>
                    <ComboboxMulti
                      options={userOptions}
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder="Select assignees"
                      dwimOnChange
                      isModal
                    />
                  </FormControl>
                  <FormDescription className="text-xs">
                    Select one or more users to assign this task to
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="due_date"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel className="text-sm font-medium">Due Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal h-10",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {field.value ? (
                            format(new Date(field.value), "PPP")
                          ) : (
                            <span>Select due date</span>
                          )}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value ? new Date(field.value) : undefined}
                        onSelect={(date) =>
                          field.onChange(date?.toISOString() || "")
                        }
                        disabled={(date) =>
                          date < new Date(new Date().setHours(0, 0, 0, 0))
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormDescription className="text-xs">
                    When the task instances are due
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.watch("assignee_ids")?.length > 1 && (
              <div className="rounded-lg border border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/30 p-4">
                <p className="text-sm text-blue-900 dark:text-blue-200">
                  <strong className="font-semibold">Note:</strong> This will create{" "}
                  {form.watch("assignee_ids").length} instance{form.watch("assignee_ids").length > 1 ? "s" : ""} of
                  this task, one for each selected assignee.
                </p>
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createInstances.isLoading || form.watch("assignee_ids")?.length === 0 || !form.watch("due_date")}
              >
                {createInstances.isLoading
                  ? "Assigning..." :
                 form.watch("assignee_ids")?.length <= 1 ? `Assign` :
                `Assign ${form.watch("assignee_ids")?.length || 0} User${form.watch("assignee_ids")?.length !== 1 ? "s" : ""}`
                }
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
